// 🔬 B251 (AI_115) — MAPA DE COMPLETUDE (puro + testável)
// ─────────────────────────────────────────────────────────────────────────────
// Versão de COORTE do checklist por-paciente da AbaAuditoria: dado o conjunto de
// RegistroPesquisa[] (pseudonimizado) de uma coorte e uma lista de campos-chave,
// calcula quantos/quais pacientes têm cada campo faltando. Serve para DIRIGIR o
// preenchimento dos prontuários (lista de tarefas). Código conta; sem IA.
//
// "Faltando" = valor null/undefined/"" (o carregador já coage faltante a null).
// Booleanos da Avaliação Clínica ENTRAM (B251-FU1): false = avaliado (presente),
// null = nunca avaliado (faltante) — exatamente o sinal de "checklist preenchido".
import { RegistroPesquisa, ValorRegistro } from "../types";

// Campo a ser avaliado (chave no registro + rótulo humano + grupo de exibição).
export interface CampoCompletude {
  chave: string;
  label: string;
  grupo: string;
}

// Resultado por campo.
export interface CompletudeCampo extends CampoCompletude {
  total: number; // n de pacientes avaliados (= tamanho da coorte)
  preenchidos: number;
  faltando: number;
  pct: number; // % preenchido (0..100, arredondado)
}

// Agrupamento (mantém a ordem em que os grupos aparecem nos campos).
export interface GrupoCompletude {
  grupo: string;
  campos: CompletudeCampo[]; // ordenados por faltando desc (mais acionável 1º)
  faltandoTotal: number; // soma de células faltantes no grupo
}

export interface CompletudeResultado {
  total: number; // n de pacientes na coorte
  nCampos: number;
  pctGeral: number; // % de células (paciente × campo) preenchidas
  campos: CompletudeCampo[]; // lista plana, ordenada por faltando desc
  grupos: GrupoCompletude[];
}

// Valor "presente" = não-faltante. Espelha temValor do carregador.
export function valorPresente(v: ValorRegistro): boolean {
  return v !== null && v !== undefined && String(v).trim() !== "";
}

export function calcularCompletude(
  registros: RegistroPesquisa[],
  campos: CampoCompletude[]
): CompletudeResultado {
  const total = registros.length;

  const porCampo: CompletudeCampo[] = campos.map((c) => {
    let preenchidos = 0;
    for (const r of registros) if (valorPresente(r[c.chave])) preenchidos++;
    const faltando = total - preenchidos;
    const pct = total > 0 ? Math.round((preenchidos / total) * 100) : 0;
    return { ...c, total, preenchidos, faltando, pct };
  });

  // % geral = células preenchidas / (n pacientes × n campos).
  const celulasTotais = total * campos.length;
  const celulasPreenchidas = porCampo.reduce((s, c) => s + c.preenchidos, 0);
  const pctGeral =
    celulasTotais > 0 ? Math.round((celulasPreenchidas / celulasTotais) * 100) : 0;

  // Grupos na ordem de aparição dos campos; dentro do grupo, mais faltantes 1º.
  const ordemGrupos: string[] = [];
  const mapaGrupo = new Map<string, CompletudeCampo[]>();
  for (const c of porCampo) {
    if (!mapaGrupo.has(c.grupo)) {
      mapaGrupo.set(c.grupo, []);
      ordemGrupos.push(c.grupo);
    }
    mapaGrupo.get(c.grupo)!.push(c);
  }
  const ordenarPorFaltando = (a: CompletudeCampo, b: CompletudeCampo) =>
    b.faltando - a.faltando || a.label.localeCompare(b.label);

  const grupos: GrupoCompletude[] = ordemGrupos.map((g) => {
    const cs = [...mapaGrupo.get(g)!].sort(ordenarPorFaltando);
    return {
      grupo: g,
      campos: cs,
      faltandoTotal: cs.reduce((s, c) => s + c.faltando, 0),
    };
  });

  const camposOrdenados = [...porCampo].sort(ordenarPorFaltando);

  return { total, nCampos: campos.length, pctGeral, campos: camposOrdenados, grupos };
}

// Pacientes (registros) da coorte que estão SEM o campo informado.
export function pacientesSemCampo(
  registros: RegistroPesquisa[],
  chave: string
): RegistroPesquisa[] {
  return registros.filter((r) => !valorPresente(r[chave]));
}
